import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import ProtectedRoute from "@/components/ProtectedRoute";
import Index from "./pages/Index.tsx";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import BetaBanner from "@/components/BetaBanner";
import FeedbackForm from "@/components/FeedbackForm";

// Lazy-load non-home routes to keep the initial bundle small (improves FCP/LCP).
const DiscoverPage = lazy(() => import("./pages/DiscoverPage.tsx"));
const LibraryPage = lazy(() => import("./pages/LibraryPage.tsx"));
const BookDetailPage = lazy(() => import("./pages/BookDetailPage.tsx"));
const AuthorsPage = lazy(() => import("./pages/AuthorsPage.tsx"));
const AuthorPublicPage = lazy(() => import("./pages/AuthorPublicPage.tsx"));
const AuthPage = lazy(() => import("./pages/AuthPage.tsx"));
const AdminPage = lazy(() => import("./pages/AdminPage.tsx"));
const AdminReportsPage = lazy(() => import("./pages/AdminReportsPage.tsx"));
const AdminPromosPage = lazy(() => import("./pages/AdminPromosPage.tsx"));
const AdminSubmissionsPage = lazy(() => import("./pages/AdminSubmissionsPage.tsx"));
const AdminLayout = lazy(() => import("./components/admin/AdminLayout.tsx"));
const AdminOverviewPage = lazy(() => import("./pages/admin/AdminOverviewPage.tsx"));
const AdminUsersPage = lazy(() => import("./pages/admin/AdminUsersPage.tsx"));
const AdminAuthorsPage = lazy(() => import("./pages/admin/AdminAuthorsPage.tsx"));
const AdminBooksPage = lazy(() => import("./pages/admin/AdminBooksPage.tsx"));
const AdminAnalyticsPage = lazy(() => import("./pages/admin/AdminAnalyticsPage.tsx"));
const AdminApplicationsPage = lazy(() => import("./pages/admin/AdminApplicationsPage.tsx"));
const AdminClaimsPage = lazy(() => import("./pages/admin/AdminClaimsPage.tsx"));
const AdminBookClaimsPage = lazy(() => import("./pages/admin/AdminBookClaimsPage.tsx"));
const AdminSettingsPage = lazy(() => import("./pages/admin/AdminSettingsPage.tsx"));
const AdminSubgenresPage = lazy(() => import("./pages/admin/AdminSubgenresPage.tsx"));
const ProfilePage = lazy(() => import("./pages/ProfilePage.tsx"));
const CommunityPage = lazy(() => import("./pages/CommunityPage.tsx"));
const PricingPage = lazy(() => import("./pages/PricingPage.tsx"));
const CheckoutReturnPage = lazy(() => import("./pages/CheckoutReturnPage.tsx"));
const SubmitBookPage = lazy(() => import("./pages/SubmitBookPage.tsx"));
const AuthorDashboardPage = lazy(() => import("./pages/AuthorDashboardPage.tsx"));
const ClaimAuthorPage = lazy(() => import("./pages/ClaimAuthorPage.tsx"));
const PromoteBookPage = lazy(() => import("./pages/PromoteBookPage.tsx"));
const DebutNewReleasesPage = lazy(() => import("./pages/DebutNewReleasesPage.tsx"));
const FaqPage = lazy(() => import("./pages/FaqPage.tsx"));
const AdminFaqsPage = lazy(() => import("./pages/admin/AdminFaqsPage.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const UnsubscribePage = lazy(() => import("./pages/UnsubscribePage.tsx"));
const AdminEmailAutomationsPage = lazy(() => import("./pages/admin/AdminEmailAutomationsPage.tsx"));
const AdminEmailsPage = lazy(() => import("./pages/admin/AdminEmailsPage.tsx"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <PaymentTestModeBanner />
          <BetaBanner />
          <FeedbackForm variant="floating" />
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/discover" element={<DiscoverPage />} />
              <Route path="/library" element={<LibraryPage />} />
              <Route path="/book/:id" element={<BookDetailPage />} />
              <Route path="/authors" element={<AuthorsPage />} />
              <Route path="/authors/:id" element={<AuthorPublicPage />} />
              <Route path="/admin/legacy" element={<AdminPage />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminOverviewPage />} />
                <Route path="users" element={<AdminUsersPage />} />
                <Route path="authors" element={<AdminAuthorsPage />} />
                <Route path="books" element={<AdminBooksPage />} />
                <Route path="subgenres" element={<AdminSubgenresPage />} />
                <Route path="promos" element={<AdminPromosPage />} />
                <Route path="analytics" element={<AdminAnalyticsPage />} />
                <Route path="applications" element={<AdminApplicationsPage />} />
                <Route path="claims" element={<AdminClaimsPage />} />
                <Route path="book-claims" element={<AdminBookClaimsPage />} />
                <Route path="faqs" element={<AdminFaqsPage />} />
                <Route path="settings" element={<AdminSettingsPage />} />
                <Route path="reports" element={<AdminReportsPage />} />
                <Route path="submissions" element={<AdminSubmissionsPage />} />
                <Route path="email-automations" element={<AdminEmailAutomationsPage />} />
                <Route path="emails" element={<AdminEmailsPage />} />
              </Route>
              <Route path="/submit-book" element={<SubmitBookPage />} />
              <Route
                path="/claim-author"
                element={
                  <ProtectedRoute>
                    <ClaimAuthorPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/author/dashboard"
                element={
                  <ProtectedRoute>
                    <AuthorDashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/author/promote"
                element={
                  <ProtectedRoute>
                    <PromoteBookPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/new-releases" element={<DebutNewReleasesPage />} />
              <Route path="/faq" element={<FaqPage />} />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route path="/community" element={<CommunityPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/checkout/return" element={<CheckoutReturnPage />} />
              <Route path="/unsubscribe" element={<UnsubscribePage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
